import { cn } from "@/lib/utils";
import type { ReportFormat } from "@/types/api";

import { metaForFormat } from "./report-format";

/**
 * The lucide icon representing a format, rendered standalone — used as the
 * no-thumbnail placeholder so image and image-less rows present the format
 * consistently.
 */
export function FormatPlaceholderIcon({
  format,
  className,
}: {
  format: ReportFormat | string;
  className?: string;
}) {
  const { Icon } = metaForFormat(format);
  return <Icon className={className} />;
}

/**
 * A compact file-format chip overlaid on the bottom-left corner of a report
 * thumbnail. Decorative (`aria-hidden`) — the same information is folded into
 * the row's accessible name via {@link formatLabel}, so screen readers hear it
 * once, in the link/button label, rather than twice.
 *
 * Bespoke rather than the shared `Badge`: `Badge` is sized for inline metadata
 * (12px text, pill padding) and would eat a third of the 80px thumbnail. This
 * sits ~16px tall on a dark scrim so it stays legible over any photo, in light
 * or dark mode.
 */
export function FormatBadge({
  format,
  className,
}: {
  format: ReportFormat | string;
  className?: string;
}) {
  const { label, Icon, iconClass } = metaForFormat(format);

  return (
    <div
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute bottom-1 left-1 z-10 flex items-center gap-0.5 rounded bg-black/70 px-1 py-0.5 backdrop-blur-sm",
        className,
      )}
    >
      <Icon className={cn("size-2.5 shrink-0", iconClass)} />
      <span className="text-[10px] font-semibold leading-none text-white">
        {label}
      </span>
    </div>
  );
}
